import { useGlobalModal } from '@/hooks/useGlobalModal';
import GlobalModal from '@components/GlobalModal';
import { UserModalTitle } from '@components/modal/UserModalTitle';
import ModalTabs, { Panel } from '@components/ModalTabs';
import { Access } from '@features/authorization';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useEffect, useState, type FC } from 'react';
import { TabSuspenseWrapper } from './common';
import { AllAccessTab } from './allAccessTab';
import { CollectionsAccessTab } from './collectionsAccessTab';
import { DeviceAccessTab } from './deviceAccessTab';
import { UserConfigurationModalTab } from './types';

export const UserConfigurationModal: FC = () => {
  const { globalModalProps, state, onClose, changeTabMiddleware, setIsDirty } = useGlobalModal(
    'userConfigurationModal',
    {
      closeConfirmation: true,
    },
  );

  const { panels, value, setValue } = usePanels({
    userId: 'userId' in state ? state.userId : null,
    initialValue: 'selectedTab' in state ? state.selectedTab ?? null : null,
    onClose,
    setIsDirty,
  });

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'xl' }}
      authorization={{
        access: Access.UpdateUser_DeviceUserAccess,
        onClose,
      }}
    >
      {state.open && (
        <>
          <UserModalTitle userId={state.userId} onClose={onClose} variant="tabs" />
          <ModalTabs
            value={value}
            setValue={setValue}
            changeTabMiddleware={changeTabMiddleware}
            panels={panels}
          />
        </>
      )}
    </GlobalModal>
  );
};

const usePanels = ({
  userId,
  initialValue,
  onClose,
  setIsDirty,
}: {
  userId: number | null;
  initialValue: UserConfigurationModalTab | null;
  onClose: () => void;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}): {
  panels: Array<Panel<UserConfigurationModalTab>>;
  value: UserConfigurationModalTab;
  setValue: React.Dispatch<React.SetStateAction<UserConfigurationModalTab>>;
} => {
  const { _ } = useLingui();
  const [selectedTab, setSelectedTab] = useState(UserConfigurationModalTab.All);

  useEffect(() => {
    // AMARU-562: resetear el tab activo cada vez que el modal se abre para un usuario
    // (incluye cerrar y reabrir para otro usuario, o el mismo) — sin `userId` en las deps,
    // el tab elegido en la sesión anterior del modal persistía entre aperturas.
    setSelectedTab(initialValue ?? UserConfigurationModalTab.All);
  }, [userId, initialValue, setSelectedTab]);

  if (!userId)
    return {
      panels: [],
      value: selectedTab,
      setValue: setSelectedTab,
    };

  const props = {
    userId,
    onClose,
    setIsDirty,
  };

  return {
    panels: [
      {
        key: UserConfigurationModalTab.All,
        label: _(msg`Todos los accesos`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <AllAccessTab userId={userId} onClose={onClose} />
          </TabSuspenseWrapper>
        ),
        access: Access.UpdateUser_DeviceUserAccess,
      },
      {
        key: UserConfigurationModalTab.Collections,
        label: _(msg`Acceso por colección`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <CollectionsAccessTab {...props} />
          </TabSuspenseWrapper>
        ),
        access: Access.UpdateUser_DeviceUserAccess,
      },
      {
        key: UserConfigurationModalTab.Access,
        label: _(msg`Acceso directo`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <DeviceAccessTab {...props} />
          </TabSuspenseWrapper>
        ),
        access: Access.UpdateUser_DeviceUserAccess,
      },
    ],
    value: selectedTab,
    setValue: setSelectedTab,
  };
};
